// src/pages/ImportClients.jsx
import React, { useState, useRef } from "react";
import * as Papa from "papaparse";
import { FiUploadCloud, FiDownloadCloud, FiLoader } from "react-icons/fi";
import { FaArrowRight } from "react-icons/fa";
import ColumnMapper from "./parts/ColumnMapper";
import ResultPanel from "./parts/ResultPanel";
import axiosClient from "../utils/apiClient.js";

const BATCH_SIZE = 100; // Number of clients to send per request

const dbFields = [
    { value: "first_name", label: "First Name (required)" },
    { value: "last_name", label: "Last Name (required)" },
    { value: "email", label: "Prymary Contact Email (required)" },
    { value: "agency", label: "Agency / Company Name" },
    { value: "contact_person", label: "Primary Contact Name" },
    { value: "phone_number", label: "Phone Number" },
    { value: "billing_address", label: "Billing Address" },
    { value: "state_or_province", label: "State" },
    { value: "county_or_region", label: "County / Region" },
    { value: "website", label: "Website" },
];

const ImportClients = () => {
    const [csvData, setCsvData] = useState([]);
    const [csvColumns, setCsvColumns] = useState([]);
    const [mapping, setMapping] = useState({});

    // Progress states
    const [parsingProgress, setParsingProgress] = useState(0);
    const [batchProgress, setBatchProgress] = useState(0);
    const [statusMessage, setStatusMessage] = useState("");

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [panelOpen, setPanelOpen] = useState(false);
    const fileRef = useRef(null);
    const [customPassword, setCustomPassword] = useState("");
    const [totalImported, setTotalImported] = useState(0);
    const [totalFailed, setTotalFailed] = useState([]);

    const resetState = () => {
        setCsvData([]);
        setCsvColumns([]);
        setMapping({});
        setParsingProgress(0);
        setBatchProgress(0);
        setStatusMessage("");
        setResult(null);
        setPanelOpen(false);
        if (fileRef.current) fileRef.current.value = "";
    };


    function detectEmailColumn(rows) {
        if (!rows.length) return null;

        const sample = rows.slice(0, 20); // check first 20 rows max
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        // Get all column names
        const columns = Object.keys(rows[0]);

        let bestMatch = null;
        let highestCount = 0;

        for (const col of columns) {
            let count = 0;

            for (const row of sample) {
                const value = row[col]?.toString().trim();
                if (value && emailRegex.test(value)) {
                    count++;
                }
            }

            // Pick the column with the highest email count
            if (count > highestCount) {
                highestCount = count;
                bestMatch = col;
            }
        }

        return bestMatch; // may return "Email", "contact_email", etc.
    }


    // ------------------------
    // Parse CSV
    // ------------------------
    const handleCSVUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        let rows = [];
        let parsed = 0;

        Papa.parse(file, {
            header: true,
            skipEmptyLines: "greedy",
            chunkSize: 1024 * 1024,
            step: (results) => {
                rows.push(results.data);
                parsed++;
                setParsingProgress((p) => Math.min(95, Math.round((parsed / (parsed + 50)) * 100)));
            },
            complete: () => {
                const emailColumn = detectEmailColumn(rows);

                const cleaned = rows.filter((row) => {
                    const email = row[emailColumn];
                    return email && String(email).trim() !== "";
                });

                console.log("csv data", cleaned)

                setCsvData(cleaned);
                const cols = Object.keys(cleaned[0] || {});
                setCsvColumns(cols);

                // Auto-map logic
                const auto = {};
                cols.forEach((col) => {
                    const lower = col.toLowerCase();
                    if (lower.includes("primary") && lower.includes("name")) auto["contact_person"] = col;
                    else if (lower.includes("primary") && lower.includes("email")) auto["email"] = col;
                    else if (lower.includes("company") && lower.includes("name")) auto["agency"] = col;
                    else if (lower.includes("phone") && lower.includes("primary")) auto["phone_number"] = col;
                    else if (lower.includes("full") && lower.includes("address")) auto["billing_address"] = col;
                    else if (lower.includes("state")) auto["state_or_province"] = col;
                    else if (lower.includes("website")) auto["website"] = col;
                    else if (lower.includes("secondary") && lower.includes("contact")) auto["other_contacts"] = col;
                });

                setMapping(auto);
                setParsingProgress(100);
            },
            error: (err) => {
                console.error("CSV parse error", err);
                setParsingProgress(0);
            }
        });
    };

    const updateMapping = (dbField, csvColumn) => {
        setMapping((prev) => ({ ...prev, [dbField]: csvColumn }));
    };

    // ------------------------
    // Build payload (MAPPING LOGIC)
    // ------------------------
    const buildPayload = () => {
        const payload = [];
        for (let row of csvData) {
            const obj = {};
            dbFields.forEach((field) => {
                const csvCol = mapping[field.value];
                if (!csvCol) return;
                const rawVal = row[csvCol] ?? "";

                if (field.value === "first_name") {
                    const parts = String(rawVal).trim().split(/\s+/);
                    obj.first_name = parts[0] || null;
                    if (!mapping["last_name"] && parts.length > 1) {
                        obj.last_name = parts.slice(1).join(" ");
                    }
                } else if (field.value === "other_contacts") {
                    const text = String(rawVal).trim();
                    if (!text) return;
                    const contacts = [];
                    const tokens = text.split(/;|,|\|/).map((t) => t.trim()).filter(Boolean);
                    for (const t of tokens) {
                        if (t.includes("@")) contacts.push({ email: t });
                        else {
                            const p = t.split(/\s+/);
                            contacts.push({ firstName: p[0], lastName: p.slice(1).join(" ") });
                        }
                    }
                    obj.other_contacts = contacts;
                } else {
                    obj[field.value] = rawVal === "" ? null : rawVal;
                }
            });

            if (!obj.agency) obj.agency = "Unknown";
            if (!obj.first_name) obj.first_name = null;
            payload.push(obj);
        }
        return payload;
    };

    const downloadCSV = (failed) => {
        if (!failed || failed.length === 0) return;
        const csv = Papa.unparse(failed);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `failed_clients_${new Date().toISOString().replace(/[:T]/g, "-")}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // ------------------------
    // Submit with BATCHING
    // ------------------------
    const handleSubmit = async () => {
        if (!csvData.length) return;

        // 1. Prepare Data
        setLoading(true);
        setBatchProgress(0);
        setResult(null);
        setPanelOpen(false);

        const allClients = buildPayload();
        const totalClients = allClients.length;

        // 2. Create Batches
        const batches = [];
        for (let i = 0; i < totalClients; i += BATCH_SIZE) {
            batches.push(allClients.slice(i, i + BATCH_SIZE));
        }

        let accumulatedFailed = [];
        let accumulatedSuccessCount = 0;
        let batchErrors = 0; // Count of batches that crashed completely

        // 3. Process Batches Sequentially
        for (let i = 0; i < batches.length; i++) {
            const currentBatch = batches[i];
            const batchNum = i + 1;

            setStatusMessage(`Processing batch ${batchNum} of ${batches.length} (${currentBatch.length} clients)...`);

            try {
                // We don't use onUploadProgress here because the requests are small and fast
                const res = await axiosClient.post(
                    "/clients/import-clients",
                    {
                        clients: currentBatch,
                        password: customPassword || null
                    }
                );

                // Aggregate results from server
                // Assuming server returns { message, failed: [], successCount: number }
                // or similar. Adjust logic to match your API response.
                if (res.data.failed && Array.isArray(res.data.failed)) {
                    accumulatedFailed = [...accumulatedFailed, ...res.data.failed];
                }

                // If server returns a specific success count, use it. 
                // Otherwise calculate based on failed length.
                const failedInBatch = res.data.failed ? res.data.failed.length : 0;
                accumulatedSuccessCount += (currentBatch.length - failedInBatch);
                // setTotalImported(accumulatedSuccessCount);
                // setTotalFailed(accumulatedFailed);
                console.log("Failed in batch:", failedInBatch, "Current Batch Size:", currentBatch.length, "Accumulated Success:", accumulatedSuccessCount);

            } catch (err) {
                console.error(`Batch ${batchNum} failed`, err);
                batchErrors++;

                // If the API call itself crashes (500, Network Error), 
                // we treat the entire batch as failed so the user can download them and try again.
                const crashedBatch = currentBatch.map(client => ({
                    ...client,
                    failure_reason: err?.response?.data?.message || "Network/Server Error during batch upload"
                }));
                accumulatedFailed = [...accumulatedFailed, ...crashedBatch];
            }

            // Update UI Progress
            const progress = Math.round((batchNum / batches.length) * 100);
            setBatchProgress(progress);
        }

        // 4. Finalize
        setLoading(false);
        setStatusMessage("Import Complete");

        setResult({
            success: accumulatedSuccessCount,
            // UPDATED: Now showing the specific number of uploaded clients in the message
            message: `Import process finished. ${accumulatedSuccessCount} clients successfully uploaded out of ${totalClients}.`,
            failed: accumulatedFailed,
            importedCount: accumulatedSuccessCount
        });

        setPanelOpen(true);
    };

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-bold text-sky-700">Import Clients</h1>
                <button
                    onClick={resetState}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    disabled={loading}
                >
                    Reset
                </button>
            </div>

            {/* Upload */}
            <label className={`w-full border-2 border-dashed border-sky-400 rounded-xl p-8 flex flex-col items-center gap-3 cursor-pointer hover:bg-sky-50 transition ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
                <FiUploadCloud className="text-5xl text-sky-600" />
                <span className="text-lg font-semibold text-sky-700">Click to upload CSV</span>
                <input ref={fileRef} type="file" accept=".csv" onChange={handleCSVUpload} className="hidden" disabled={loading} />
            </label>

            {/* Parsing progress */}
            {parsingProgress > 0 && parsingProgress < 100 && (
                <div className="mt-4">
                    <div className="text-sm text-gray-600 mb-1">Parsing CSV — {parsingProgress}%</div>
                    <div className="w-full bg-gray-200 h-2 rounded">
                        <div className="h-2 rounded bg-sky-600" style={{ width: `${parsingProgress}%` }} />
                    </div>
                </div>
            )}

            {/* MAPPING (DB → CSV) */}
            {csvColumns.length > 0 && !loading && !panelOpen && (
                <div className="mt-8">
                    <h2 className="text-2xl font-semibold">Column Mapping</h2>
                    <p className="text-sm mb-3">Note: Exported Client CSV from ServeManager or any other Sites will be automatically mapped here.</p>

                    <div className="grid gap-3">
                        {dbFields.map((field) => (
                            <ColumnMapper
                                key={field.value}
                                dbField={field}
                                csvColumns={csvColumns}
                                selected={mapping[field.value] || ""}
                                onChange={updateMapping}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Preview & Password */}
            {csvData.length > 0 && !loading && !panelOpen && (
                <div className="mt-6">
                    <label className="block text-gray-700 font-medium mb-2">
                        Optional Password for All Clients
                    </label>
                    <input
                        type="text"
                        value={customPassword}
                        onChange={(e) => setCustomPassword(e.target.value)}
                        placeholder="Leave blank to auto-generate"
                        className="w-full p-3 border rounded-lg bg-gray-50"
                    />
                </div>
            )}

            {/* Submit / Batch Progress Area */}
            {csvData.length > 0 && (
                <div className="mt-6">
                    {!loading ? (
                        !panelOpen && (
                            <button
                                onClick={handleSubmit}
                                className="px-6 py-3 bg-sky-700 text-white font-bold rounded-lg flex items-center gap-2 hover:bg-sky-800 transition"
                            >
                                Import {csvData.length} Clients <FaArrowRight />
                            </button>
                        )
                    ) : (
                        <div className="w-full bg-sky-50 border border-sky-100 p-6 rounded-xl">
                            <div className="flex items-center gap-3 mb-3">
                                <FiLoader className="animate-spin text-sky-600 text-xl" />
                                <span className="font-semibold text-sky-800">
                                    {statusMessage || "Initializing upload..."}
                                </span>
                            </div>

                            <div className="w-full bg-gray-200 h-4 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-sky-600 transition-all duration-300 ease-out flex items-center justify-end pr-2"
                                    style={{ width: `${batchProgress}%` }}
                                >
                                    <span className="text-[10px] text-white font-bold">{batchProgress}%</span>
                                </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-2 text-center">
                                Do not close this window until the process completes.
                            </p>
                        </div>
                    )}
                </div>
            )}

            <ResultPanel
                open={panelOpen}
                onClose={() => setPanelOpen(false)}
                result={result}
                onDownloadFailed={() => downloadCSV(result?.failed || [])}
            />
        </div>
    );
};

export default ImportClients;